import { AccountAccess } from './account-access';
import { AdviserCrm, accountBotsSchema } from './adviser-crm';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import {
  adviserDirectorySchema,
  adviserDetailSchema,
  calendarAccountsSchema,
  adviserInputSchema,
  type Adviser,
  type AdviserInput,
  type BotScope,
} from '@cryptology-assist/domain';
import {
  request,
  ApiError,
  idSchema,
  savedSchema,
  providersSchema,
  type Session,
} from './api';
import { useRemote } from './hooks';
import { ErrorNotice, Field, onSubmit, useMutation } from './components';
import { useDraftNavigation } from './draft-navigation';
import { prepareNavigation } from './navigation';
import type { Copy, Language } from './copy';

const calendarsSchema = z.object({
  calendars: z.array(
    z.object({ id: z.string(), name: z.string(), writable: z.boolean() })
  ),
});
const advisersSavedSchema = z.object({ id: z.string(), revision: z.string() });

export function Advisers({
  scope,
  session,
  copy,
  language,
}: {
  scope: BotScope;
  session: Session;
  copy: Copy;
  language: Language;
}) {
  const [after, setAfter] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const path = `/v1/accounts/${scope.companyId}`;
  const directory = useRemote(
    `${path}/advisers${after ? `?after=${after}` : ''}`,
    adviserDirectorySchema
  );
  const detail = useRemote(
    editing ? `${path}/advisers/${editing}` : null,
    adviserDetailSchema
  );
  const navigation = useMutation();
  async function close() {
    await prepareNavigation();
    setEditing(null);
    setAdding(false);
    directory.reload();
  }
  return (
    <section className="settings-content">
      <div className="section-heading">
        <h1>{copy.accountTeam}</h1>
        <p>{copy.adviserDirectoryHelp}</p>
      </div>
      <ErrorNotice
        error={directory.error ?? detail.error ?? navigation.error}
        copy={copy}
        onRetry={() => {
          directory.reload();
          detail.reload();
        }}
      />
      {!adding && !editing && (
        <>
          <div className="section-toolbar">
            <h2>{copy.adviserDirectory}</h2>
            <button
              className="primary"
              onClick={() =>
                void navigation.run(async () => {
                  await prepareNavigation();
                  setAdding(true);
                })
              }
            >
              {copy.addAdviser}
            </button>
          </div>
          {directory.loading && !directory.data && <p>{copy.loading}</p>}
          {directory.data?.advisers.length === 0 && <p>{copy.noAdvisers}</p>}
          {directory.data?.advisers.map((person) => (
            <div className="connection-row" key={person.id}>
              <div>
                <strong>{person.name}</strong>
                <p>{person.email}</p>
                <span className="small muted">
                  {person.active ? copy.adviserActive : copy.adviserInactive} ·{' '}
                  {person.timezone}
                </span>
              </div>
              <button
                onClick={() =>
                  void navigation.run(async () => {
                    await prepareNavigation();
                    setEditing(person.id);
                  })
                }
              >
                {copy.edit}
              </button>
            </div>
          ))}
          <div className="form-actions">
            {after && (
              <button onClick={() => setAfter(null)}>{copy.firstPage}</button>
            )}
            {directory.data?.nextCursor && (
              <button onClick={() => setAfter(directory.data!.nextCursor)}>
                {copy.nextPage}
              </button>
            )}
          </div>
        </>
      )}
      {!editing && !adding && (
        <AccountAccess
          companyId={scope.companyId}
          session={session}
          copy={copy}
          language={language}
        />
      )}
      {editing && detail.loading && !detail.data && <p>{copy.loading}</p>}
      {(adding || (editing && detail.data)) && (
        <AdviserEditor
          key={editing ?? 'new'}
          scope={scope}
          session={session}
          copy={copy}
          language={language}
          person={detail.data?.adviser ?? null}
          onSaved={(personId) => {
            setAdding(false);
            setEditing(personId);
            detail.reload();
            directory.reload();
          }}
          onClose={close}
        />
      )}
      {editing && detail.data && (
        <AccountAccess
          companyId={scope.companyId}
          person={detail.data.adviser}
          session={session}
          copy={copy}
          language={language}
        />
      )}
      {editing && detail.data && (
        <AdviserCrm
          scope={scope}
          person={detail.data.adviser}
          session={session}
          copy={copy}
        />
      )}
      {editing && detail.data && (
        <AdviserCalendars
          scope={scope}
          adviserId={editing}
          detail={detail.data}
          session={session}
          copy={copy}
          reload={detail.reload}
        />
      )}
    </section>
  );
}

function AdviserEditor({
  scope,
  session,
  copy,
  language,
  person,
  onSaved,
  onClose,
}: {
  scope: BotScope;
  session: Session;
  copy: Copy;
  language: Language;
  person: Adviser | null;
  onSaved: (id: string) => void;
  onClose: () => Promise<void>;
}) {
  const bots = useRemote(
    `/v1/accounts/${scope.companyId}/bots`,
    accountBotsSchema
  );
  const [value, setStoredValue] = useState<AdviserInput>(() =>
    person
      ? { ...person, expectedRevision: person.revision }
      : {
          expectedRevision: null,
          name: '',
          email: null,
          active: true,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          weeklyHours: [],
          exceptions: [],
          botIds: [],
        }
  );
  const [dirty, setDirty] = useState(false);
  const valueRef = useRef(value);
  const editVersion = useRef(0);
  const saving = useRef<Promise<void> | null>(null);
  const observedPerson = useRef(person?.revision);
  function setValue(next: AdviserInput) {
    valueRef.current = next;
    editVersion.current++;
    setStoredValue(next);
    setDirty(true);
  }
  useEffect(() => {
    if (person && person.revision !== observedPerson.current) {
      observedPerson.current = person.revision;
      if (!dirty) {
        const next = { ...person, expectedRevision: person.revision };
        valueRef.current = next;
        setStoredValue(next);
      }
    }
  }, [person, dirty]);
  const mutation = useMutation();
  const [error, setError] = useState<Error | null>(null);
  function save(): Promise<void> {
    if (saving.current) return saving.current;
    const work = (async () => {
      let version: number;
      let savedId: string;
      do {
        version = editVersion.current;
        const {
          id,
          expectedRevision,
          name,
          email,
          active,
          timezone,
          weeklyHours,
          exceptions,
          botIds,
        } = valueRef.current;
        const input = adviserInputSchema.parse({
          ...(id ? { id } : {}),
          expectedRevision,
          name,
          email,
          active,
          timezone,
          weeklyHours,
          exceptions,
          botIds,
        });
        const result = await request(
          `/v1/accounts/${scope.companyId}/advisers`,
          advisersSavedSchema,
          { csrf: session.csrf, body: input }
        );
        savedId = result.id;
        valueRef.current = {
          ...valueRef.current,
          id: result.id,
          expectedRevision: result.revision,
        };
        setStoredValue(valueRef.current);
      } while (version !== editVersion.current);
      setDirty(false);
      setError(null);
      onSaved(savedId);
    })()
      .catch((error: unknown) => {
        const failure =
          error instanceof z.ZodError
            ? new ApiError(
                'ADVISER_FORM_INVALID',
                422,
                error.issues.map((issue) => `adviser.${issue.path.join('.')}`)
              )
            : error instanceof Error
              ? error
              : new Error('REQUEST_FAILED');
        setError(failure);
        throw failure;
      })
      .finally(() => {
        saving.current = null;
      });
    saving.current = work;
    return work;
  }
  useDraftNavigation(dirty, save);
  const days = Array.from({ length: 7 }, (_, day) =>
    new Intl.DateTimeFormat(language === 'es' ? 'es-PE' : 'en-US', {
      weekday: 'long',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(2026, 0, 4 + day)))
  );
  return (
    <form
      className="adviser-editor"
      onChange={() => setDirty(true)}
      onSubmit={(event) => onSubmit(event, () => mutation.run(save))}
    >
      <fieldset disabled={mutation.busy}>
        <h2>{person ? copy.editAdviser : copy.addAdviser}</h2>
        <ErrorNotice error={bots.error} copy={copy} onRetry={bots.reload} />
        <div className="form-grid">
          <Field label={copy.adviserName}>
            <input
              required
              maxLength={150}
              value={value.name}
              onChange={(event) =>
                setValue({ ...value, name: event.target.value })
              }
            />
          </Field>
          <Field label={copy.adviserEmail}>
            <input
              type="email"
              value={value.email ?? ''}
              onChange={(event) =>
                setValue({ ...value, email: event.target.value || null })
              }
            />
          </Field>
        </div>
        <label className="check-row">
          <input
            type="checkbox"
            checked={value.active}
            onChange={(event) =>
              setValue({ ...value, active: event.target.checked })
            }
          />
          {copy.adviserAcceptsBookings}
        </label>
        <section className="form-section">
          <h3>{copy.authorizedAssistants}</h3>
          <p className="small muted">{copy.adviserGrantHelp}</p>
          <div className="checks">
            {bots.data?.bots.map((member) => (
              <label key={member.id}>
                <input
                  type="checkbox"
                  checked={value.botIds.includes(member.id)}
                  onChange={(event) =>
                    setValue({
                      ...value,
                      botIds: event.target.checked
                        ? [...value.botIds, member.id]
                        : value.botIds.filter((id) => id !== member.id),
                    })
                  }
                />
                {member.name}
              </label>
            ))}
          </div>
        </section>
        <section className="form-section">
          <h3>{copy.adviserWorkingHours}</h3>
          <Field label={copy.timezone}>
            <select
              value={value.timezone}
              onChange={(event) =>
                setValue({ ...value, timezone: event.target.value })
              }
            >
              {[
                ...new Set([
                  value.timezone,
                  ...Intl.supportedValuesOf('timeZone'),
                ]),
              ].map((timezone) => (
                <option key={timezone}>{timezone}</option>
              ))}
            </select>
          </Field>
          {value.weeklyHours.length === 0 && (
            <p className="notice-inline">{copy.noAdviserHours}</p>
          )}
          {value.weeklyHours.map((window, index) => (
            <div className="working-window" key={index}>
              <Field label={copy.weekday}>
                <select
                  value={window.day}
                  onChange={(event) =>
                    setValue({
                      ...value,
                      weeklyHours: value.weeklyHours.map((entry, position) =>
                        position === index
                          ? { ...entry, day: Number(event.target.value) }
                          : entry
                      ),
                    })
                  }
                >
                  {days.map((day, position) => (
                    <option key={day} value={position}>
                      {day}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={copy.opensAt}>
                <input
                  type="time"
                  required
                  value={window.opensAt}
                  onChange={(event) =>
                    setValue({
                      ...value,
                      weeklyHours: value.weeklyHours.map((entry, position) =>
                        position === index
                          ? { ...entry, opensAt: event.target.value }
                          : entry
                      ),
                    })
                  }
                />
              </Field>
              <Field label={copy.closesAt}>
                <input
                  type="time"
                  required
                  value={window.closesAt}
                  onChange={(event) =>
                    setValue({
                      ...value,
                      weeklyHours: value.weeklyHours.map((entry, position) =>
                        position === index
                          ? { ...entry, closesAt: event.target.value }
                          : entry
                      ),
                    })
                  }
                />
              </Field>
              <button
                type="button"
                onClick={() => {
                  setValue({
                    ...value,
                    weeklyHours: value.weeklyHours.filter(
                      (_, position) => position !== index
                    ),
                  });
                  setDirty(true);
                }}
              >
                {copy.remove}
              </button>
            </div>
          ))}
          <button
            type="button"
            disabled={value.weeklyHours.length >= 21}
            onClick={() => {
              setValue({
                ...value,
                weeklyHours: [
                  ...value.weeklyHours,
                  { day: 1, opensAt: '', closesAt: '' },
                ],
              });
              setDirty(true);
            }}
          >
            {copy.addWorkingWindow}
          </button>
        </section>
        <section className="form-section">
          <h3>{copy.availabilityExceptions}</h3>
          <p>{copy.availabilityExceptionsHelp}</p>
          {value.exceptions.map((exception, index) => (
            <div className="edit-panel" key={index}>
              <Field label={copy.exceptionDate}>
                <input
                  type="date"
                  required
                  value={exception.date}
                  onChange={(event) =>
                    setValue({
                      ...value,
                      exceptions: value.exceptions.map((entry, position) =>
                        position === index
                          ? { ...entry, date: event.target.value }
                          : entry
                      ),
                    })
                  }
                />
              </Field>
              {exception.windows.length === 0 && (
                <p>{copy.unavailableAllDay}</p>
              )}
              {exception.windows.map((window, position) => (
                <div className="form-grid" key={position}>
                  {(['opensAt', 'closesAt'] as const).map((key) => (
                    <Field key={key} label={copy[key]}>
                      <input
                        type="time"
                        required
                        value={window[key]}
                        onChange={(event) =>
                          setValue({
                            ...value,
                            exceptions: value.exceptions.map((entry, i) =>
                              i === index
                                ? {
                                    ...entry,
                                    windows: entry.windows.map((item, j) =>
                                      j === position
                                        ? { ...item, [key]: event.target.value }
                                        : item
                                    ),
                                  }
                                : entry
                            ),
                          })
                        }
                      />
                    </Field>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setValue({
                        ...value,
                        exceptions: value.exceptions.map((entry, i) =>
                          i === index
                            ? {
                                ...entry,
                                windows: entry.windows.filter(
                                  (_, j) => j !== position
                                ),
                              }
                            : entry
                        ),
                      })
                    }
                  >
                    {copy.removeWorkingWindow}
                  </button>
                </div>
              ))}
              <div className="form-actions">
                <button
                  type="button"
                  disabled={exception.windows.length >= 3}
                  onClick={() => {
                    setValue({
                      ...value,
                      exceptions: value.exceptions.map((entry, i) =>
                        i === index
                          ? {
                              ...entry,
                              windows: [
                                ...entry.windows,
                                { opensAt: '', closesAt: '' },
                              ],
                            }
                          : entry
                      ),
                    });
                    setDirty(true);
                  }}
                >
                  {copy.addWorkingWindow}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setValue({
                      ...value,
                      exceptions: value.exceptions.filter(
                        (_, i) => i !== index
                      ),
                    });
                    setDirty(true);
                  }}
                >
                  {copy.remove}
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            disabled={value.exceptions.length >= 100}
            onClick={() => {
              setValue({
                ...value,
                exceptions: [...value.exceptions, { date: '', windows: [] }],
              });
              setDirty(true);
            }}
          >
            {copy.addAvailabilityException}
          </button>
        </section>
        <ErrorNotice error={error ?? mutation.error} copy={copy} />
        <div className="form-actions sticky-form-actions">
          <button className="primary">{copy.saveAdviser}</button>
          <button
            type="button"
            onClick={() =>
              void mutation.run(async () => {
                if (dirty) await save();
                await onClose();
              })
            }
          >
            {copy.backToAdvisers}
          </button>
        </div>
      </fieldset>
    </form>
  );
}

function AdviserCalendars({
  scope,
  adviserId,
  detail,
  session,
  copy,
  reload,
}: {
  scope: BotScope;
  adviserId: string;
  detail: typeof adviserDetailSchema._output;
  session: Session;
  copy: Copy;
  reload: () => void;
}) {
  const base = `/v1/accounts/${scope.companyId}`;
  const accounts = useRemote(
    `${base}/calendar-accounts`,
    calendarAccountsSchema
  );
  const providers = useRemote('/v1/providers', providersSchema);
  const [connectionId, setConnectionId] = useState('');
  const [calendarId, setCalendarId] = useState('');
  const calendars = useRemote(
    connectionId ? `${base}/calendar-accounts/${connectionId}/calendars` : null,
    calendarsSchema
  );
  const mutation = useMutation();
  return (
    <section className="form-section">
      <h2>{copy.adviserCalendars}</h2>
      <p>{copy.adviserCalendarsHelp}</p>
      <ErrorNotice
        error={
          accounts.error ?? calendars.error ?? providers.error ?? mutation.error
        }
        copy={copy}
        onRetry={() => {
          accounts.reload();
          calendars.reload();
          providers.reload();
        }}
      />
      {detail.calendars.map((calendar) => (
        <div className="connection-row" key={calendar.id}>
          <div>
            <strong>{calendar.name}</strong>
            <p>
              {calendar.provider} · {calendar.address}
            </p>
            <span className="small muted">
              {calendar.writable
                ? copy.calendarCanBook
                : copy.calendarConflictOnly}
            </span>
          </div>
          <button
            disabled={mutation.busy}
            onClick={() =>
              void mutation.run(async () => {
                await request(
                  `${base}/advisers/${adviserId}/calendars/${calendar.id}`,
                  savedSchema,
                  { method: 'DELETE', csrf: session.csrf, body: {} }
                );
                reload();
              })
            }
          >
            {copy.remove}
          </button>
        </div>
      ))}
      {accounts.loading && !accounts.data && <p>{copy.loading}</p>}
      {accounts.data && (
        <form
          className="edit-panel"
          onSubmit={(event) =>
            onSubmit(event, () =>
              mutation.run(async () => {
                await request(
                  `${base}/advisers/${adviserId}/calendars`,
                  idSchema,
                  { csrf: session.csrf, body: { connectionId, calendarId } }
                );
                setCalendarId('');
                reload();
              })
            )
          }
        >
          <Field label={copy.calendarAccount}>
            <select
              required
              value={connectionId}
              onChange={(event) => {
                setConnectionId(event.target.value);
                setCalendarId('');
              }}
            >
              <option value="">{copy.selectCalendarAccount}</option>
              {accounts.data.connections
                .filter((account) => account.status === 'ready')
                .map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.address} · {account.provider}
                  </option>
                ))}
            </select>
          </Field>
          {connectionId && (
            <Field label={copy.calendar}>
              <select
                required
                value={calendarId}
                onChange={(event) => setCalendarId(event.target.value)}
              >
                <option value="">{copy.selectCalendar}</option>
                {calendars.data?.calendars.map((calendar) => (
                  <option key={calendar.id} value={calendar.id}>
                    {calendar.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <button disabled={mutation.busy || !calendarId}>
            {copy.addAdviserCalendar}
          </button>
        </form>
      )}
      <div className="connect-actions">
        {providers.data?.providers.map((provider) => (
          <a
            key={provider}
            href={`${base}/calendar-authorizations/${provider}?botId=${scope.botId}`}
          >
            {copy.connectCalendarAccount} · {copy[provider]}
          </a>
        ))}
      </div>
      <p className="small muted">{copy.calendarOAuthHelp}</p>
    </section>
  );
}
